import React, { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Spin, Button, Drawer, Form, Input, message, Modal, Tabs } from "antd";
import styled from "styled-components";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { BuildingOffice, DotsThreeVertical, User } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../atoms/LanguageSwitcher";
import { useResponsive } from "../../hooks/useResponsive";
import { BranchInfo, BranchInfoRef } from "../organisms/settings-branch-info";
import { OnlineSalesSettings } from "../organisms/settings-online-sales/OnlineSalesSettings";
import { DeliveryMethods } from "../organisms/settings-delivery-methods/DeliveryMethods";
import { PaymentMethods, PaymentMethodsRef } from "../organisms/settings-payment-methods/PaymentMethods";
import { GlobalSettings } from "../organisms/settings-global/GlobalSettings";
import { ChangePassword } from "../organisms/settings-change-password/ChangePassword";
import { DeleteAccount } from "../organisms/settings-delete-account";
import { CompanyDataModal } from "../organisms/company-data/CompanyData";
import { getCurrentUser, updateCurrentUser } from "../../service/client";
import { useBranchContext } from "../../contexts/branch-context";
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import { StyledTabs, PillTabs } from "../../styles/shared-components";
import { colors, spacing, sizes } from "../../styles/theme";
import { AppearanceSettings } from "../organisms/settings-appearance/AppearanceSettings";
import { PosSettings } from "../organisms/settings-pos/PosSettings";
import { PrinterSettings } from "../organisms/settings-printer/PrinterSettings";
import { BillingSettings } from "../organisms/settings-billing/BillingSettings";
import { getCompany } from "../../service/client";

const SettingsWrapper = styled.div`
  padding: ${spacing.xxl} 40px;
  padding-bottom: 80px;
  background: ${colors.white};
  min-height: calc(100vh - 64px);
  overflow-x: clip;

  @media (max-width: 768px) {
    padding: ${spacing.md};
    padding-bottom: 80px;
    overflow-x: clip;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${spacing.xl};
`;

const TitleBlock = styled.div``;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0 0 ${spacing.md} 0;
`;

const BranchName = styled.div`
  font-size: 14px;
  color: ${colors.textSecondary};
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 24px;
`;

const TabContent = styled.div`
  max-width: 800px;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
  }
`;

const TabContentFullWidth = styled.div`
  width: 100%;
`;


const StyledAccountModal = styled(Modal)`
  .ant-modal {
    top: 50% !important;
    transform: translateY(-50%) !important;
    padding-bottom: 0 !important;
  }

  .ant-modal-content {
    display: flex;
    flex-direction: column;
    height: 70vh;
    max-height: 70vh;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal-header {
    flex-shrink: 0;
    padding: 20px ${spacing.xl} 0;
  }

  .ant-modal-body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
  }
`;

const AccountTabsNav = styled.div`
  flex-shrink: 0;
  padding: ${spacing.lg} ${spacing.xl} 0;

  .ant-tabs-nav {
    margin-bottom: 0 !important;
  }
`;

const AccountScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing.xl};
  padding-bottom: 80px;
`;

const AccountModalFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  padding: ${spacing.lg} ${spacing.xl};
  background: ${colors.white};
  border-top: 1px solid ${colors.borderLight};
  border-radius: 0 0 ${sizes.borderRadiusSmall} ${sizes.borderRadiusSmall};
  z-index: 1;
`;

export const Settings: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: currentUser, isLoading } = useQuery(
    ["/admin/current_user"],
    () => getCurrentUser()
  );

  const { currentBranch } = useBranchContext();
  const { isMobile } = useResponsive();
  const { registerChecker, unregisterChecker, setModalHandler } = useUnsavedChanges();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeKey, setActiveKey] = useState(() => searchParams.get("tab") || "online-sales");

  // Check if company is blocked
  const { data: companyData } = useQuery(
    ["/company"],
    () => getCompany()
  );
  const companyBlocked = companyData?.isActive === false;

  // Apply URL params on first render, then clear them
  useEffect(() => {
    const tab = searchParams.get("tab");
    const sub = searchParams.get("sub");
    if (tab || sub) {
      if (tab) setActiveKey(tab);
      if (sub) setOnlineSalesSubTab(sub);
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Force billing tab when blocked
  useEffect(() => {
    if (companyBlocked) {
      setActiveKey("billing");
    }
  }, [companyBlocked]);
  const activeKeyRef = useRef(activeKey);
  const onlineSalesSubTabRef = useRef("sales");
  const branchInfoRef = useRef<BranchInfoRef>(null);
  const paymentsRef = useRef<PaymentMethodsRef>(null);
  const [onlineSalesSubTab, setOnlineSalesSubTab] = useState(() => searchParams.get("sub") || "sales");
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [accountTabKey, setAccountTabKey] = useState("info");
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [infoForm] = Form.useForm();

  const isAdmin = currentUser?.role === "ADMIN";

  const updateUserMutation = useMutation(updateCurrentUser, {
    onSuccess: () => {
      message.success(t("settings.account.updateSuccess"));
      queryClient.invalidateQueries(["/admin/current_user"]);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.error || t("settings.account.updateError"));
    },
  });

  const handleInfoSave = useCallback(async () => {
    try {
      const values = await infoForm.validateFields();
      await updateUserMutation.mutateAsync(values);
    } catch { }
  }, [infoForm, updateUserMutation]);

  // Keep refs in sync so closures in registerChecker always see current values
  useEffect(() => {
    activeKeyRef.current = activeKey;
  }, [activeKey]);

  useEffect(() => {
    onlineSalesSubTabRef.current = onlineSalesSubTab;
  }, [onlineSalesSubTab]);

  // Register P24 guard in global UnsavedChanges context so navbar navigation is also intercepted
  useEffect(() => {
    registerChecker(() => {
      return activeKeyRef.current === "online-sales" && onlineSalesSubTabRef.current === "payments" && !!(paymentsRef.current?.hasUnconfiguredP24());
    });
    setModalHandler((onConfirm) => {
      paymentsRef.current?.showP24WarnModal(onConfirm);
    });
    return () => {
      unregisterChecker();
      setModalHandler(null);
    };
  }, [registerChecker, unregisterChecker, setModalHandler]);

  // Populate info form when modal opens or user data arrives
  React.useEffect(() => {
    if (accountModalOpen && currentUser) {
      infoForm.setFieldsValue({
        firstName: currentUser.firstName || "",
        lastName: currentUser.lastName || "",
      });
    }
  }, [accountModalOpen, currentUser, infoForm]);

  if (isLoading) {
    return (
      <SettingsWrapper>
        <Spin />
      </SettingsWrapper>
    );
  }

  const handleTabChange = (key: string) => {
    if (activeKey === "branch-info" && branchInfoRef.current?.hasUnsavedChanges()) {
      branchInfoRef.current.showUnsavedChangesModal(() => {
        setActiveKey(key);
      });
      return;
    }
    if (activeKey === "online-sales" && onlineSalesSubTab === "payments" && paymentsRef.current?.hasUnconfiguredP24()) {
      paymentsRef.current.showP24WarnModal(() => {
        setActiveKey(key);
      });
      return;
    }
    setActiveKey(key);
  };

  const accountTabItems = [
    { key: "info", label: t("settings.account.tabInfo") },
    { key: "password", label: t("settings.account.tabPassword") },
    { key: "delete", label: t("settings.account.tabDelete") },
  ];

  const isWorker = currentUser?.role === "WORKER";

  const handleSubTabChange = (key: string) => {
    if (onlineSalesSubTab === "payments" && paymentsRef.current?.hasUnconfiguredP24()) {
      paymentsRef.current.showP24WarnModal(() => {
        setOnlineSalesSubTab(key);
      });
      return;
    }
    setOnlineSalesSubTab(key);
  };

  const onlineSalesSubItems = [
    {
      key: "sales",
      label: t("settings.subTabs.sales"),
      children: <TabContent><OnlineSalesSettings /></TabContent>,
    },
    ...(!isWorker ? [
      {
        key: "delivery",
        label: t("settings.subTabs.delivery"),
        children: <TabContentFullWidth><DeliveryMethods /></TabContentFullWidth>,
      },
      {
        key: "payments",
        label: t("settings.subTabs.payments"),
        children: <TabContentFullWidth><PaymentMethods ref={paymentsRef} /></TabContentFullWidth>,
      },
    ] : []),
  ];

  const rawItems = [
    {
      key: "online-sales",
      label: t("settings.tabs.onlineSales"),
      children: (
        <PillTabs
          activeKey={onlineSalesSubTab}
          onChange={handleSubTabChange}
          items={onlineSalesSubItems}
          size="middle"
        />
      ),
    },
    {
      key: "pos",
      label: t("settings.tabs.pos"),
      children: <TabContent><PosSettings /></TabContent>,
    },
    {
      key: "notifications",
      label: t("settings.tabs.notifications"),
      children: <TabContent><GlobalSettings /></TabContent>,
    },
    {
      key: "branch-info",
      label: t("settings.tabs.branch"),
      children: <TabContent><BranchInfo ref={branchInfoRef} /></TabContent>,
    },
    {
      key: "printer",
      label: t("settings.tabs.printer"),
      children: <TabContent><PrinterSettings /></TabContent>,
    },
    {
      key: "appearance",
      label: t("settings.tabs.appearance"),
      children: <TabContent><AppearanceSettings /></TabContent>,
    },
    {
      key: "billing",
      label: t("settings.tabs.billing"),
      children: <TabContentFullWidth><BillingSettings /></TabContentFullWidth>,
    },
  ];

  // When company is blocked, only show billing tab
  const items = companyBlocked
    ? rawItems.filter(item => item.key === "billing")
    : rawItems.filter(item => {
      if (currentUser?.role !== "WORKER") return true;
      return ["online-sales", "appearance", "printer"].includes(item.key);
    });

  return (
    <SettingsWrapper>
      <HeaderRow>
        <TitleBlock>
          <PageTitle>{t("settings.title")}</PageTitle>
          {currentBranch && (
            <BranchName>{currentBranch.name}</BranchName>
          )}
        </TitleBlock>
        <HeaderButtons>
          <LanguageSwitcher size={isMobile ? "small" : "middle"} />
          {isMobile ? (
            <Button
              icon={<DotsThreeVertical size={22} weight="bold" />}
              onClick={() => setSettingsDrawerOpen(true)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          ) : (
            <>
              <Button
                type="link"
                icon={<User size={18} weight="regular" />}
                onClick={() => setAccountModalOpen(true)}
                style={{ padding: "4px 0", color: colors.primary }}
              >
                {t("common.yourAccount")}
              </Button>
              {isAdmin && (
                <Button
                  type="link"
                  icon={<BuildingOffice size={18} weight="regular" />}
                  onClick={() => setCompanyModalOpen(true)}
                  style={{ padding: "4px 0", color: colors.primary }}
                >
                  {t("common.companySettings")}
                </Button>
              )}
            </>
          )}
        </HeaderButtons>
      </HeaderRow>
      <StyledTabs
        activeKey={activeKey}
        onChange={handleTabChange}
        items={items}
        size="large"
      />

      <CompanyDataModal
        open={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        isAdmin={isAdmin}
      />

      {/* Mobile 3-dot drawer */}
      <Drawer
        title={null}
        placement="bottom"
        open={settingsDrawerOpen}
        onClose={() => setSettingsDrawerOpen(false)}
        height="auto"
        styles={{ header: { display: 'none' }, body: { padding: 0 } }}
      >
        <Button type="text" block onClick={() => { setSettingsDrawerOpen(false); setAccountModalOpen(true); }} style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: "1px solid var(--color-divider)", borderRadius: 0, color: "var(--color-text)" }}>
          <User size={20} />
          {t("common.yourAccount")}
        </Button>
        {isAdmin && (
          <Button type="text" block onClick={() => { setSettingsDrawerOpen(false); setCompanyModalOpen(true); }} style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 0, color: "var(--color-text)" }}>
            <BuildingOffice size={20} />
            {t("common.companySettings")}
          </Button>
        )}
      </Drawer>

      <StyledAccountModal
        title={t("common.yourAccount")}
        open={accountModalOpen}
        onCancel={() => setAccountModalOpen(false)}
        footer={null}
        width={600}
        centered
      >
        <AccountTabsNav>
          <Tabs
            activeKey={accountTabKey}
            onChange={setAccountTabKey}
            items={accountTabItems}
            size="small"
          />
        </AccountTabsNav>
        <AccountScrollArea>
          {accountTabKey === "info" ? (
            <Form form={infoForm} layout="vertical">
              <Form.Item
                name="firstName"
                label={t("settings.account.firstName")}
                rules={[{ required: true, message: t("settings.account.required") }]}
              >
                <Input placeholder={t("settings.account.firstName")} />
              </Form.Item>
              <Form.Item
                name="lastName"
                label={t("settings.account.lastName")}
                rules={[{ required: true, message: t("settings.account.required") }]}
              >
                <Input placeholder={t("settings.account.lastName")} />
              </Form.Item>
            </Form>
          ) : accountTabKey === "password" ? (
            <ChangePassword />
          ) : (
            <DeleteAccount />
          )}
        </AccountScrollArea>
        <AccountModalFooter>
          {accountTabKey === "info" && (
            <Button
              type="primary"
              onClick={handleInfoSave}
              loading={updateUserMutation.isLoading}
            >
              {t("settings.account.save")}
            </Button>
          )}
        </AccountModalFooter>
      </StyledAccountModal>
    </SettingsWrapper>
  );
};

export default Settings;
